import {
  Button,
  Card,
  Checkbox,
  DateRangePicker,
  InputSelect,
  Label,
} from '@features/ui';
import { useCallback, useState } from 'react';
import { DateValueType, downloadFile } from '@lib/utils.ts';
import { findDuplicatesBy } from '@features/admin/users/usersData.ts';
import {
  DuplicateUserRes,
  FindDuplicatesByType,
  UserReq,
} from '@src/types/users.ts';
import { DuplicateCard } from '@features/admin/users/DuplicateCard.tsx';
import {
  exportAddressDuplicateUser,
  exportPhonesDuplicateUser,
  getDuplicateUser,
} from '@src/services/users.ts';
import { useNavigate } from 'react-router-dom';

export const DuplicateUsers = () => {
  const [requiredExpire, setRequiredExpire] = useState(false);
  const [isCreateExcel, setIsCreateExcel] = useState(false);
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [selectedDuplicatesType, setSelectedDuplicatesType] =
    useState<FindDuplicatesByType>(FindDuplicatesByType.PhoneNumber);
  const [duplicateUsers, setDuplicateUsers] = useState<DuplicateUserRes[]>([]);

  const navigate = useNavigate();

  const fetchUsers = async (params?: UserReq) => {
    const res = await getDuplicateUser(params);

    setDuplicateUsers(res.data);
  };

  const handleFilter = useCallback(async () => {
    if (!valueStart?.startDate) {
      setRequiredExpire(true);
    }

    if (!selectedDuplicatesType || !valueStart?.startDate) return;
    await fetchUsers({
      duplicate_by: selectedDuplicatesType,
      expire_date_after: new Date(valueStart.startDate).toISOString(),
    });
  }, [selectedDuplicatesType, valueStart?.startDate]);

  const handleExport = useCallback(async () => {
    if (!valueStart?.startDate) {
      setRequiredExpire(true);
    }

    if (!selectedDuplicatesType || !valueStart?.startDate) return;

    const params = {
      duplicate_by: selectedDuplicatesType || undefined,
      expire_date_after: valueStart?.startDate
        ? new Date(valueStart.startDate).toISOString()
        : undefined,
    };

    if (selectedDuplicatesType === FindDuplicatesByType.PhoneNumber) {
      const res = await exportPhonesDuplicateUser(params);

      downloadFile(res.data, 'duplicate-users');

      return;
    }
    const resAddress = await exportAddressDuplicateUser(params);

    downloadFile(resAddress.data, 'duplicate-users');
  }, [selectedDuplicatesType, valueStart?.startDate]);

  return (
    <Card>
      <div
        className={
          'flex lg:flex-row flex-col w-full mb-5 gap-6 flex-wrap lg:items-end'
        }
      >
        <div className={'flex flex-col gap-y-2'}>
          <Label className={'text-xs'}>Find duplicates by</Label>
          <InputSelect
            className={'lg:w-[239px] w-full border-[#C6C6C6]'}
            options={findDuplicatesBy}
            placeholder={'Select'}
            value={selectedDuplicatesType}
            onChange={(value) =>
              setSelectedDuplicatesType(value as FindDuplicatesByType)
            }
          />
        </div>
        <div className={'flex flex-col gap-y-2 text-xs'}>
          <div className={'flex gap-1'}>
            <Label className={'text-xs'}>
              Where user expire date is greater than
            </Label>
            {requiredExpire && (
              <div className={'px-[2px] rounded-[2px] bg-red-500'}>
                Required
              </div>
            )}
          </div>
          <DateRangePicker
            hideHeader
            asSingle={true}
            className={'lg:w-[300px]'}
            size={'sm'}
            value={valueStart}
            variant={'outline'}
            onChange={setValueStart}
          />
        </div>
        <div className={'flex flex-col gap-1'}>
          <Checkbox
            checked={isCreateExcel}
            id={`checkbox-create-excel`}
            label={'Create Excel for Export?'}
            onCheckedChange={(checked) => setIsCreateExcel(Boolean(checked))}
          />
          <p className={'text-xs'}>
            Warning - this substantially slows down this report!
          </p>
        </div>
        <div className={'mb-1 flex gap-3'}>
          <Button size={'sm'} variant={'lime'} onClick={handleFilter}>
            Find
          </Button>
          {isCreateExcel && (
            <Button size={'sm'} variant={'secondary'} onClick={handleExport}>
              Export Data
            </Button>
          )}
        </div>
      </div>
      <div className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
        {duplicateUsers.map((item, index) => (
          <DuplicateCard
            key={`${item.phone_number}_${item.address}_${index}`}
            duplicateUser={item}
            onClick={(id) => navigate(`/admin/users/${id}`)}
          />
        ))}
      </div>
    </Card>
  );
};
